import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';

import { RootState } from 'store/store';
import { createSafeAsyncThunk } from 'store/store.helper';
import { getUnpinnedHighlightsForStory, HighlightDto } from 'services/highlight.service';

const SLICE_ID = 'highlights';

/**
 * Map of story ID to unpinned highlights
 */
export type HighlightsMap = Record<StoryId, HighlightDto[]>;

interface State {
  unpinnedHighlights: HighlightsMap;
  loading: Record<StoryId, boolean>;
  error: Record<StoryId, string | null>;
}

const initialState: State = {
  unpinnedHighlights: {},
  loading: {},
  error: {},
};

// A S Y N C   T H U N K S

/**
 * Fetches unpinned highlights for a story
 */
export const fetchUnpinnedHighlightsForStory = createSafeAsyncThunk(
  `${SLICE_ID}/fetchUnpinnedHighlightsForStory`,
  async (storyId: StoryId, { rejectWithValue }) => {
    try {
      const response = await getUnpinnedHighlightsForStory(storyId);
      return { storyId, highlights: response.data || [] };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch highlights';
      return rejectWithValue({ storyId, error: errorMessage });
    }
  }
);

// S L I C E

const highlightsSlice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    /**
     * Clears highlights for a specific story
     */
    clearHighlightsForStory: (state, action: PayloadAction<StoryId>) => {
      const storyId = action.payload;
      delete state.unpinnedHighlights[storyId];
      delete state.loading[storyId];
      delete state.error[storyId];
    },
    /**
     * Clears all highlights
     */
    clearAllHighlights: (state) => {
      state.unpinnedHighlights = {};
      state.loading = {};
      state.error = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnpinnedHighlightsForStory.pending, (state, action) => {
        const storyId = action.meta.arg;
        state.loading[storyId] = true;
        state.error[storyId] = null;
      })
      .addCase(fetchUnpinnedHighlightsForStory.fulfilled, (state, action) => {
        const { storyId, highlights } = action.payload as { storyId: StoryId; highlights: HighlightDto[] };
        state.unpinnedHighlights[storyId] = highlights;
        state.loading[storyId] = false;
        state.error[storyId] = null;
      })
      .addCase(fetchUnpinnedHighlightsForStory.rejected, (state, action) => {
        const { storyId, error } = action.payload as { storyId: StoryId; error: string };
        state.loading[storyId] = false;
        state.error[storyId] = error;
      });
  },
});

// S E L E C T O R S

/**
 * Selects unpinned highlights for a story
 */
export const selectUnpinnedHighlightsForStory = (storyId: StoryId) =>
  createSelector(
    (state: RootState) => state.highlights.unpinnedHighlights[storyId],
    (highlights) => highlights || []
  );

/**
 * Selects loading state for highlights of a story
 */
export const selectHighlightsLoading = (storyId: StoryId) =>
  createSelector(
    (state: RootState) => state.highlights.loading[storyId],
    (loading) => loading || false
  );

/**
 * Selects error state for highlights of a story
 */
export const selectHighlightsError = (storyId: StoryId) =>
  createSelector(
    (state: RootState) => state.highlights.error[storyId],
    (error) => error || null
  );

// E X P O R T S

export const { clearHighlightsForStory, clearAllHighlights } = highlightsSlice.actions;
export default highlightsSlice.reducer;
